import { UIChartBar } from '../bar/ui-chart-bar';
import { Component } from '../../../shared-components/decorators/component.decorator';
import { Attribute } from '../../../shared-components/decorators/attribute.decorator';
import type { Point } from 'highcharts';
import { ExtendedPlotSeriesOptions } from '../charts.types';
import styles from './ui-chart-lineargauge.css';

/**
 * @class
 * @memberof ChartsComponents
 * @element ui-chart-lineargauge
 * @augments {UIChart}
 * @alias UIChartLinearGauge
 * @classdesc Represents a class for <code>ui-chart-lineargauge</code> element.
 * Draws lineargauge type chart.
 * @property {boolean} percentage {@attr percentage} Display value as percent.
 * @slot {@type "ui-series"}
 * @example
 * <ui-chart-lineargauge units="EUR" height="125">
 *   <ui-series name="Expenses" data="[82]" class="highcharts-color-4"></ui-series>
 * </ui-chart-lineargauge>
 */
@Component({ tag: 'ui-chart-lineargauge', styles })
export class UIChartLinearGauge extends UIChartBar {
    @Attribute(Boolean) percentage?: boolean;

    private min: number = 0;
    private max: number = 100;

    /**
     * @inheritDoc
     */
    override renderChart(): void {
        this.mergeOptionsFromElement();
        const yAxis = this.opts?.yAxis;
        if (yAxis && !Array.isArray(yAxis)) {
            this.min = Number(yAxis.min ?? 0);
            this.max = Number(yAxis.max ?? 100);
        } else {
            this.min = 0;
            this.max = 100;
        }
        const { max, percentage, units } = this;
        const seriesOptions: ExtendedPlotSeriesOptions = {
            borderRadius: 2,
            dataLabels: {
                enabled: true,
                useHTML: true,
                x: -20,
                y: -83 /* -74 - 4 -5 (padding)  highcharts 8.1.1+ issue */,
                formatter: function (this: Point): string {
                    if (percentage) {
                        return Math.floor(((this.y ?? 0) / max) * 100) + '%';
                    }
                    return (this.y ?? 0) + ' ' + units;
                },
            },
        };
        const responsiveSeriesOptions: ExtendedPlotSeriesOptions = {
            pointWidth: 30,
            dataLabels: {
                y: -24,
            },
        };

        this.draw({
            chart: {
                marginRight: 0,
            },
            xAxis: {
                visible: true,
                opposite: true,
                labels: {
                    useHTML: true,
                    align: 'right',
                    x: 0,
                    y: 60,
                    formatter: (): string => {
                        const seriesOpts = this.getOptions().series?.[0];
                        return (
                            '<div class="highcharts-data-label lineargauge-axis-numeric-label">' +
                            max +
                            ' ' +
                            units +
                            '</div>' +
                            '<div class="lineargauge-axis-label">' +
                            (seriesOpts?.name ?? '') +
                            '</div>'
                        );
                    },
                },
                plotBands: [
                    {
                        from: -0.248,
                        to: 0.248,
                    },
                ],
            },
            yAxis: {
                min: this.min,
                max: this.max,
            },
            plotOptions: {
                series: seriesOptions,
            },
            responsive: {
                rules: [
                    {
                        condition: {
                            maxWidth: 400,
                        },
                        chartOptions: {
                            plotOptions: {
                                series: responsiveSeriesOptions,
                            },
                            xAxis: {
                                plotBands: [
                                    {
                                        from: -0.17,
                                        to: 0.163,
                                    },
                                ],
                            },
                        },
                    },
                ],
            },
        });
    }
}
